import type { ThemeOptions } from '@mui/material/styles'

/**
 * 1854 MUI theme foundations — the shareable Layer-1 core: palette + typography.
 *
 * Values are aligned to the 1854 base theme (from the team leading the
 * 1854 → MUI transition), NOT the T3/Core external-facing tokens. This module is
 * deliberately shaped to be lifted into a shared `@equipmentshare/1854-*` package
 * later — so it stays foundations-only:
 *   - NO component `styleOverrides` (those are Layer 3 — per-app, per the stop-line)
 *   - NO density `defaultProps` (Layer 2 — app policy; inventory sets compact itself)
 *   - NO domain palettes from the source theme (deliveryType / blueGrey / panelBackground)
 *
 * NOTE: these VALUES are provisional pending confirmation of the canonical 1854
 * token source. The structure is stable; only the hexes/scale should change.
 */

// Grey ramp (stock-MUI scale, as used by the 1854 base theme). Exported so app
// slots (e.g. the neutral ramp) can reference it instead of re-hardcoding hex.
export const GREY = {
	50: '#fafafa',
	100: '#f5f5f5',
	200: '#eeeeee',
	300: '#e0e0e0',
	400: '#bdbdbd',
	500: '#9e9e9e',
	600: '#757575',
	700: '#616161',
	800: '#424242',
	900: '#212121',
}

export const foundations = {
	palette: {
		mode: 'light',
		primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
		secondary: { main: '#757575' },
		success: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
		info: { main: '#0288d1', light: '#03a9f4', dark: '#01579b' },
		warning: { main: '#ed6c02', light: '#ffc107' },
		error: { main: '#d32f2f' },
		grey: GREY,
		background: {
			default: '#f2f5f7', // shared neutral surface (also T3 COLOR_SHADE.LIGHTEST)
			paper: '#ffffff',
		},
	},
	// Font is Inter (shared with T3). The scale is a light override on MUI defaults
	// — the base theme themes only a few variants; the rest fall through to MUI.
	//
	// Sizes are `px`, not `rem` (sc-563618): these values were copied from a
	// reference theme authored assuming a standard 16px document root, but EUI's
	// own global reset (mounted alongside MUI for the whole coexistence period)
	// sets `html { font-size: 14px }` — a rem literal silently renders at 87.5%
	// of the size its author intended. `px` is immune to the host document's
	// root font-size, whoever sets it, which also makes this module more
	// portable for its stated goal of becoming a shared package.
	typography: {
		fontFamily: 'Inter, sans-serif',
		h1: {
			fontFeatureSettings: '\'liga\' off, \'clig\' off',
			fontSize: '32px',
			fontWeight: 700,
			lineHeight: '123.5%',
			letterSpacing: '0.25px',
		},
		h2: {
			fontFeatureSettings: '\'liga\' off, \'clig\' off',
			fontSize: '25.6px',
			fontWeight: 600,
			lineHeight: '123.5%',
			letterSpacing: '0.25px',
		},
		// The base theme themed only h1/h2, leaving h3–h6 at MUI's (larger) defaults
		// — a non-monotonic scale. Completed here from the values their own
		// theme-reference.md documents (h3–h5), plus an h6 below h5.
		h3: { fontSize: '22px', fontWeight: 600 },
		h4: { fontSize: '20px', fontWeight: 600 },
		h5: { fontSize: '16px', fontWeight: 600 },
		h6: { fontSize: '14px', fontWeight: 600 },
		subtitle1: {
			fontSize: '20px',
			fontWeight: 500,
			lineHeight: 1.57,
			letterSpacing: '0.1px',
		},
		subtitle2: {
			fontSize: '14px',
			fontWeight: 500,
			lineHeight: 1.57,
			letterSpacing: '0.1px',
		},
	},
} satisfies ThemeOptions
